import { dataExpTagConfig } from '@/components/product/const';
import { useTranslation } from '@/i18n/client';
import { EXPOSURE_RECORD } from '@/tracks/25217';
import { Fragment } from 'react';
import { AddCartButton, BuyNowButton, NotifyButton, ReserveButton } from './buttons';
import UKLoadingIcon, { CIRCLE_COLOR_MAP, SIZE_MAP } from '@/components/UKLoadingIcon';
export interface CommonBtnProps {
  onClickEvent: () => void;
  btnName: string;
  disabled: boolean;
  id: string;
  loading?: boolean;
}

export interface BtnDataProps {
  btnId: string;
  btnName: string;
  status: string;
}

export enum BtnEventIds {
  notify = 'notify', // 到货通知
  buyNow = 'buyNow', // 立即购买
  addToCart = 'addToCart', // 加入购物车
  reserveNow = 'reserveNow', // 立即预约
}
export interface BtnConfigItem {
  event: () => void;
  loading?: boolean;
}

// btnConfig 的完整类型
type BtnConfig = {
  [key in BtnEventIds]: BtnConfigItem;
};

export const ArrivalNotification = ({
  onClickEvent,
  btnName,
  disabled,
  loading,
}: CommonBtnProps) => {
  return (
    <NotifyButton
      size='large'
      disabled={disabled}
      disableRipple
      startIcon={
        loading ? <UKLoadingIcon size={SIZE_MAP.SIZE_20} type={CIRCLE_COLOR_MAP.BLACK} /> : null
      }
      onClick={() => !disabled && onClickEvent?.()}
    >
      {loading ? '' : btnName}
    </NotifyButton>
  );
};

export const ReserveBtn = ({ onClickEvent, btnName, disabled }: CommonBtnProps) => {
  return (
    <ReserveButton size='large' disabled={disabled} onClick={() => !disabled && onClickEvent?.()}>
      {btnName}
    </ReserveButton>
  );
};
export const BuyNow = ({ onClickEvent, btnName, disabled, loading }: CommonBtnProps) => {
  return (
    <BuyNowButton
      size='large'
      disabled={disabled}
      disableRipple
      onClick={() => !disabled && onClickEvent?.()}
      exptag={'exp|' + EXPOSURE_RECORD.Productdetail_GotoBuyNow_Expo}
      data-exptag-config={dataExpTagConfig}
      startIcon={
        loading ? <UKLoadingIcon size={SIZE_MAP.SIZE_20} type={CIRCLE_COLOR_MAP.WHITE} /> : null
      }
    >
      {loading ? '' : btnName}
    </BuyNowButton>
  );
};

export const AddToCart = ({ onClickEvent, btnName, disabled, loading }: CommonBtnProps) => {
  return (
    <AddCartButton
      size='large'
      disabled={disabled}
      disableRipple
      onClick={() => !disabled && onClickEvent?.()}
      exptag={'exp|' + EXPOSURE_RECORD.Productdetail_Addtocart_Expo}
      data-exptag-config={dataExpTagConfig}
      startIcon={
        loading ? <UKLoadingIcon size={SIZE_MAP.SIZE_20} type={CIRCLE_COLOR_MAP.WHITE} /> : null
      }
    >
      {loading ? '' : btnName}
    </AddCartButton>
  );
};

export interface ComponentMap {
  component: typeof ArrivalNotification | typeof BuyNow | typeof AddToCart;
  eventId: BtnEventIds;
}

// btns 接口下发文档: https://joyspace.jd.com/pages/WUiyilBvEVt97c9zjdBl
const btnsConfig = [
  // 到货通知
  ...['btn_notify', 'btn_notified'].map((item) => [
    item,
    {
      component: ArrivalNotification,
      eventId: BtnEventIds.notify,
    },
  ]),
  // 立即购买
  ...['btn_buynow', 'btn_buynow_pickup', 'btn_buynow_bigitem'].map((item) => [
    item,
    {
      component: BuyNow,
      eventId: BtnEventIds.buyNow,
    },
  ]),
  ...['btn_addtocart', 'btn_addtocart_pickup', 'btn_addtocart_bigitem', 'btn_addtocart_gift'].map(
    (item) => [
      item,
      {
        component: AddToCart,
        eventId: BtnEventIds.addToCart,
      },
    ],
  ),
  // 预售 - 等待预约期、立即预约、已预约
  ...[
    'btn_reservation_notstarted',
    'btn_reservation_notreservation',
    'btn_reservation_reservation',
  ].map((item) => [
    item,
    {
      component: ReserveBtn,
      eventId: BtnEventIds.reserveNow,
    },
  ]),
];
const BtnsConfigMap = new Map<string, ComponentMap>(btnsConfig as [string, ComponentMap][]);
const queryComponentByUniqueId = (btnId: string) => {
  return BtnsConfigMap.get(btnId);
};

export default function BottomButtonLayout(props: { data: BtnDataProps[]; btnConfig: BtnConfig }) {
  const { data = [], btnConfig } = props;
  const { t } = useTranslation();
  const btnKeys = Array.from(BtnsConfigMap.keys());
  // 匹配 key
  const matchKeyArray = data.filter((item) => btnKeys.includes(item.btnId));

  if (data.length === 0 || matchKeyArray.length === 0) {
    // 后端数据下发异常，默认展示加车、立即购买
    const buyNowId = 'btn_buynow';
    const addToCartId = 'btn_addtocart';
    const buyNow = queryComponentByUniqueId(buyNowId);
    const addToCart = queryComponentByUniqueId(addToCartId);
    return (
      <>
        {buyNow?.component && (
          <buyNow.component
            id={buyNowId}
            disabled={false}
            btnName={t('dp.buyNow')}
            onClickEvent={btnConfig?.[buyNow.eventId]?.event}
            loading={btnConfig?.[buyNow?.eventId]?.loading}
          />
        )}
        {addToCart?.component && (
          <addToCart.component
            id={addToCartId}
            disabled={false}
            btnName={t('dp.addToCart')}
            onClickEvent={btnConfig?.[addToCart.eventId]?.event}
            loading={btnConfig?.[addToCart.eventId]?.loading}
          />
        )}
      </>
    );
  }
  return (
    <>
      {data.map((item) => {
        const current: ComponentMap | undefined = queryComponentByUniqueId(item.btnId);
        return (
          <Fragment key={item.btnId}>
            {current?.component && (
              <current.component
                key={item.btnId}
                id={item.btnId}
                disabled={item.status === '0'} // 1:可用; 0:不可用
                btnName={item.btnName}
                onClickEvent={btnConfig?.[current?.eventId]?.event}
                loading={btnConfig?.[current?.eventId]?.loading}
              />
            )}
          </Fragment>
        );
      })}
    </>
  );
}
